<template>
  <PageContent>
    <ProductDetailSkeleton
      v-if="fetching"
      :with-variant-box="true"
      :with-text-info-box="true"
      :with-attributes="true"
    />
    <div v-else-if="product && (parseInt(packageUnit, 10) || 0) > -1">
      <ProductDetailTwoColumns
        :product="product"
        :selected-variant="selectedVariant"
        :get-attribute="getProductAttribute(product, selectedVariant)"
        :has-siblings="hasSiblings"
        :product-detail-properties="PRODUCT_DETAIL_ATTRIBUTE_KEYS_EYEWEAR"
        :detail-name-mapping="detailNameMapping"
        :back-url="backUrl"
        :product-name="productName"
        :product-color="productColor"
        @on-vto-action="onVtoAction"
      >
        <template #actions>
          <div class="flex flex-col gap-6">
            <div class="price-summary border-b border-gray-350 pb-6">
              <ProductPrice
                size="2xl"
                :price="price"
                no-margin
                :applied-reductions="price.appliedReductions"
                :show-price-from="hasSpecial"
                :show-price-reduction-badge="hasSpecial"
                :show-discount="true"
                data-testid="product-price"
              />

              <ProductDetailDescriptionText>
                <span class="mt-2 inline-block text-xs leading-5">
                  <template v-if="price.withTax > 0">
                    {{ $t('pdp.glasses.with_tax') + ' ' }}
                  </template>
                  <ProductShippingCosts
                    :price="price"
                    :quantity="quantity"
                    :product-id="product.id"
                    :is-rx="isRxConfiguratorAvailable"
                    text-size="xs"
                  />
                  <span>
                    {{ ` – ${$t('pdp.delivery_location_information')}` }}
                  </span>
                  <span v-if="lowestPrice" class="flex text-gray-600">
                    {{ $t('pdp.lowest_price') }}
                    {{ `${lowestPrice}` }}
                  </span>
                </span>
              </ProductDetailDescriptionText>
            </div>
            <ProductDetailSizePicker
              v-if="!isMonoLens"
              :package-unit="parseInt(packageUnit, 10) || 0"
              :package-units="packageUnits"
              :has-diopters="everyVariantHasDiopters(product.variants)"
              @update:package-unit="onSwitchVariant"
            />
            <FaceScan :variant="selectedVariant" />

            <div v-if="hasSiblings" class="w-full">
              <Headline tag="span" look="h5">
                {{ $t('pdp.colors_heading') }}
              </Headline>
              <ProductDetailSiblingSlider
                class="mt-3 max-w-[100vw]"
                :product="product"
                :ignore-sold-out="false"
              />
            </div>
            <div
              v-if="
                product.variants && everyVariantHasDiopters(product.variants)
              "
              class="mt-5 w-full"
            >
              <ProductDetailVariantSlider
                class="mt-3 max-w-[100vw]"
                :product="product"
              />
            </div>
            <div>
              <ProductPromotionGiftConditionBanner
                v-if="!areGiftConditionsMet && isGiftAddedToBasket"
                :product="product"
              />
              <ProductPromotionBanners v-else :product="product" />
              <div class="h-fit rounded-lg bg-gray-150 px-5 py-6 lg:px-8">
                <ProductDetailDeliveryEstimates
                  :key="deliveryEstimatesKey"
                  :stock="stock"
                  :sales-status="salesStatus"
                  :icon-size="2.5"
                  :is-rx-glasses="isRxConfiguratorAvailable"
                  :product="product"
                  class="mb-4 justify-center !font-normal"
                />

                <FimButton
                  data-testid="add-to-cart"
                  type="primary"
                  class="mb-0 w-full"
                  :disabled="isA2bDisabled"
                  :loading="isAddingToCart"
                  @click="addItemToBasket"
                >
                  {{ $t('pdp.add_label') }}
                  <template #iconAfter>
                    <LazyIconFielmannCart class="mr-2 size-6" />
                  </template>
                </FimButton>
                <FimButton
                  v-if="isRxConfiguratorAvailable"
                  data-testid="openRxConfigurator"
                  :disabled="
                    !isProductAvailable(product) ||
                    !isVariantAvailable(product, selectedVariant)
                  "
                  class="mt-4 w-full"
                  type="secondary"
                  @mousedown="openRxConfigurator"
                >
                  {{
                    $t('pdp.correctional_glasses.with_glasses_from', {
                      combinedPrice,
                    })
                  }}

                  <template #iconAfter>
                    <LazyIconFielmannArrowRight class="size-6" />
                  </template>
                </FimButton>

                <ProductPromotionGifts
                  v-if="isBuyXGetYPrioritized && !isGiftAddedToBasket"
                  :product="product"
                  class="mt-6"
                />

                <div
                  class="my-2 text-center text-[15px] font-normal text-gray-600"
                >
                  <span>{{ $t('pdp.correctional_glasses.or') }}</span>
                </div>

                <FimButton
                  :type="isRxConfiguratorAvailable ? 'tertiary' : 'secondary'"
                  class="w-full"
                  :to="appointmentUrl"
                  target="_blank"
                >
                  {{ $t('pdp.schedule_appointment_button') }}</FimButton
                >
              </div>
              <BenefitsList class="mt-6" :slug="categorySlug" />
              <RxConfiguratorOverlay
                v-if="rxConfiguratorProps"
                :config="rxConfiguratorProps"
                @add-to-basket="rxConfiguratorAddToBasket"
                @close="closeRxConfigurator"
              />
            </div>
          </div>
        </template>
        <template #manuals>
          <div v-if="manualUrl || complianceUrl">
            <Headline class="mb-6" tag="span" look="h3">
              {{ $t('pdp.manual.headline') }}
            </Headline>
            <ProductDetailManualLink
              v-if="manualUrl"
              class="mb-6"
              :url="manualUrl"
              translation-key="pdp.manual.copyGlasses"
            />
            <ProductDetailManualLink
              v-if="complianceUrl"
              :url="complianceUrl"
              translation-key="pdp.manual.copyCompliance"
              link-translation-key="pdp.manual.linkCompliance"
            />
          </div>
        </template>
      </ProductDetailTwoColumns>
      <div
        v-if="isSyteEnabled && syteProductImageUrl"
        id="syte-similar-items-container"
        :data-sku="selectedVariantId[0] ?? product.id"
        :data-image-src="syteProductImageUrl"
        data-placement="PDP"
      ></div>
      <FimRecommendations
        v-if="recommendationRefXsell"
        :recommendation-product="product"
        :product-recommendations="recommendationRefXsell"
        :title="getRecommendationTitle('recommendationRefXsell', $i18n)"
        class="mt-12"
        location="PDP"
      >
        <FimRecommendationLinks :field-name="recommendationLinkFieldName" />
      </FimRecommendations>
      <BazaarVoice :id="product.id" show="reviews" />
      <ClientOnly>
        <ProductDetailCartOverlay
          :recommendation-product="product"
          :product-recommendations="productRecommendations"
        >
          <FimRecommendationLinks :field-name="recommendationLinkFieldName" />
        </ProductDetailCartOverlay>
      </ClientOnly>
    </div>
  </PageContent>
</template>

<script setup lang="ts">
import type {
  BasketItemDisplayData,
  Product,
  Variant,
} from '@scayle/storefront-api'
import { isEmpty } from 'radash'
import { DisplayDataAttributes } from '~/composables/pdp/pdpTypes'
import { useGlasses } from '~/composables/pdp/useGlasses'
import useUiState from '~/composables/ui/useUiState'
import { useAppointments } from '~/composables/useAppointments'
import { useFimBasket } from '~/composables/useFimBasket'
import { PRODUCT_DETAIL_ATTRIBUTE_KEYS_EYEWEAR } from '~/constants/product'
import { PRODUCT_WITH_GLASSES } from '~/constants/withParams'
import type { RecommendationLinkFieldNames } from '~/storyblok/types/storyblok'
import { getColors, getFrameSizesForVariant } from '~/utils/glasses'
import { isReadyMadeGlasses } from '~/utils/pdp'
import { getLowestPriorPrice } from '~/utils/price'
import {
  getRecommendationTitle,
  isProductAvailable,
  isVariantAvailable,
  isVariantMaybeFitting,
  ProductCategories,
} from '~/utils/product'
import { getQueryParam } from '~/utils/route'
import {
  generateBreadcrumbSchema,
  generateProductSchema,
  getBreadcrumbSchemaItems,
  getProductHead,
} from '~/utils/seo'

const { setPageState } = usePageState()
const scope = effectScope()
onScopeDispose(() => scope.stop())
const props = defineProps({
  baseProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
})

const context = useNuxtApp()
const { $i18n, $localeRoute, $fimLocalePath } = context
const currentShop = useCurrentShop()
const { data: shopCustomData } = useCustomData()
const { faceWidth } = useFaceWidth()
const route = useRoute()
const { trackPageView } = useTrackingEvents()
const { getProductDetailRoute } = useRouteHelpers()
const queryVariantId = computed(() => getQueryParam(route.query, 'variantid'))

const checkAvailability = () => {
  // if user navigates from PLP to out of stock variant, redirect to first in stock variant
  const cheapestVariant = variantsSortedByPrice.value?.[0]
  if (
    (!queryVariantId.value ||
      parseInt(queryVariantId.value, 10) === cheapestVariant?.id) &&
    (!isVariantAvailable(product.value, cheapestVariant) ||
      !isVariantMaybeFitting(cheapestVariant, faceWidth))
  ) {
    const cheapestVariantAvailable =
      variantsSortedByPrice.value.find(
        (v) =>
          isVariantAvailable(product.value, v) &&
          isVariantMaybeFitting(v, faceWidth),
      ) ||
      variantsSortedByPrice.value.find((v) =>
        isVariantAvailable(product.value, v),
      )

    if (cheapestVariantAvailable) {
      const newRoute = getProductDetailRoute(product.value, {
        ...route.query,
        variantid: cheapestVariantAvailable.id,
      })

      const localeRoute = $localeRoute(newRoute)

      if (localeRoute) {
        router.replace(localeRoute)
      }
    }
  }
}

const {
  product: detailedProduct,
  price,
  quantity,
  fetching,
  packageUnit,
  selectedVariant,
  hasSiblings,
  hasSpecial,
  salesStatus,
  trackProductView,
  inStock,
  stock,
  breadcrumbs,
  backUrl,
  manualUrl,
  complianceUrl,
  productName,
  variantsSortedByPrice,
  isSellableWithoutStock,
  setVariantId,
} = await useProductDetails(props.baseProduct, {
  params: {
    includeSellableForFree: true,
    with: PRODUCT_WITH_GLASSES,
  },
  options: { immediate: true },
})

const onVtoAction = ({
  product,
  variant,
}: {
  product: Product
  variant: Variant
}) => {
  addItemToBasket({ product, variant, source: 'virtual_try_on' })
}

const { trackAddToBasket } = useTrackingEvents()
const localePath = useFimLocalePath()
const product = computed(() => detailedProduct.value || props.baseProduct)

const { getCombinedFrameAndCheapestLensPrice } = useProductPrice()

const combinedPrice = computed(() => {
  const value = getCombinedFrameAndCheapestLensPrice(
    product.value,
    selectedVariant.value,
  )
  return toCurrency(value?.withTax ?? 0)
})

const {
  isBuyXGetYPrioritized,
  isGiftAddedToBasket,
  areGiftConditionsMet,
  highestPriorityPromotion,
} = useProductPromotions(product)

const {
  openRxConfigurator,
  closeRxConfigurator,
  addRxItemsToBasket,
  isRxConfiguratorAvailable,
  rxConfiguratorProps,
} = useRxConfigurator(product, selectedVariant)

// Product recommendations
const { data: productRecommendations } = useAsyncProductRecommendations(
  [...PRODUCT_RECOMMENDATION_ATTRIBUTES],
  product.value,
)

const recommendationRefXsell = computed(
  () => productRecommendations.value?.recommendationRefXsell,
)

const isA2bDisabled = computed(() => {
  return (
    !isProductAvailable(product.value) ||
    !isVariantAvailable(product.value, selectedVariant.value)
  )
})

const head = computed(() =>
  getProductHead(product.value, selectedVariant.value, price.value, context),
)

useHead(() => head.value)
onMounted(() => {
  trackProductView(product.value)

  trackPageView({
    title: head.value.title,
    pageType: 'product',
  })
})

const { productColor, packageUnits, isMonoLens } = useGlasses(
  product,
  variantsSortedByPrice,
)

const router = useRouter()
const { isSyteEnabled } = useFeatureToggles()

const { addItem } = useFimBasket()
const image = useImage()

const isAddingToCart = ref(false)
const { getAppointmentUrl } = useAppointments()
// // Appointment URL for AT only on this PDP should point to a specific service
// // https://aboutyou.atlassian.net/browse/SCFIM-1577?focusedCommentId=4393825
const serviceSlug = computed(() =>
  currentShop.value.shopId === 10001 ? 'GL_BE' : 'GL_CA',
)
const appointmentUrl = computed(() => getAppointmentUrl(serviceSlug.value))

const { openBasketOverlay } = useUiState()
const selectedVariantId = computed(() =>
  selectedVariant.value ? [selectedVariant.value.id] : [],
)

// todo: cleanup useProductDetails
const onSwitchVariant = (index: number) => {
  const entry = packageUnits.value?.[index]
  if (!product.value || entry?.disabled) {
    return
  }
  const variantid =
    entry?.variantId === variantsSortedByPrice.value?.[0]?.id
      ? undefined
      : entry.variantId

  if (entry?.variantId) {
    setVariantId(entry.variantId)
  }
  router.replace(
    $fimLocalePath(
      getProductDetailRoute(product.value, {
        ...route.query,
        variantid,
      }),
    ),
  )
}

const syteProductImageUrl = computed(() => {
  if (product.value) {
    return image(product.value.images[0].hash, { trim: 1 }, { provider: 'fim' })
  }
  return undefined
})

const getCurrentVariant = () => {
  const variantId = getQueryParam(router.currentRoute.value.query, 'variantId')
  return (
    (product.value.variants ?? []).find(
      (variant) => variant.id.toString() === variantId,
    ) ?? variantsSortedByPrice.value[parseInt(packageUnit.value, 10) || 0]
  )
}

const isConsultantUsed = computed(
  () => getQueryParam(route.query, 'consultant', '0') === '1',
)

const rxConfiguratorAddToBasket = async ({
  itemsSerialized,
  rxConfiguration,
}: {
  itemsSerialized: ConfiguratorSerialized
  rxConfiguration: ConfiguratorState
}) => {
  const promotionId =
    highestPriorityPromotion.value?.id && !isBuyXGetYPrioritized.value
      ? highestPriorityPromotion.value.id
      : undefined

  try {
    await addRxItemsToBasket(
      itemsSerialized,
      rxConfiguration,
      promotionId,
      quantity,
    )
    router.push(localePath(routeList.basket))
  } catch (error) {
    console.error('[RXCONFIGURATOR ADD]: ', getErrorMessage(error))
  }
}

const addItemToBasket = async (payload?: {
  product: Product
  variant: Variant
  source?: string
}) => {
  if (
    !isAddingToCart.value &&
    variantsSortedByPrice.value &&
    variantsSortedByPrice.value.length
  ) {
    isAddingToCart.value = true

    const variant = getCurrentVariant()

    const a2bProduct =
      payload?.product && payload?.variant ? payload.product : toValue(product)
    const a2bVariant =
      payload?.product && payload?.variant ? payload.variant : toValue(variant)

    const displayDataPayload: { displayData: BasketItemDisplayData } = {
      displayData: {
        [DisplayDataAttributes.colorAttribute]: {
          key: 'colors',
          label: $i18n.t('pdp.colors_heading'),
          value: getColors(a2bProduct),
        },
      },
    }

    if (
      a2bVariant.stock?.quantity === 0 &&
      a2bVariant.stock?.isSellableWithoutStock
    ) {
      displayDataPayload.displayData[
        DisplayDataAttributes.deliveryForecastAttribute
      ] = {
        key: $i18n.t('basket.displayData.deliveryForecastFallback.key'),
        label: $i18n.t('basket.displayData.deliveryForecastFallback.label'),
        value:
          shopCustomData.value?.deliveryEstimatesProductType
            ?.sellableWithoutStockAdditionalTime ??
          $i18n.t('basket.displayData.deliveryForecastFallback.value'),
      }
    }

    const diopters = getVariantDiopters(a2bVariant)
    if (diopters?.value) {
      // @ts-expect-error attribute-4 can be used but it's not available in sfc types
      displayDataPayload.displayData['attribute-4'] = {
        key: 'diopters',
        label: $i18n.t('reading_aids_selector.diopter'),
        value: diopters.label,
      }
    }

    const frameSizes = getFrameSizesForVariant(a2bVariant)
    if (frameSizes) {
      displayDataPayload.displayData[DisplayDataAttributes.sizeAttribute] = {
        key: 'frameSizes',
        label: $i18n.t('basket_card.size_label'),
        value: frameSizes,
      }
    }

    const promotionId = highestPriorityPromotion.value?.id
    const branchId = getBranchIdFromSession()

    const customData = {
      ...(branchId && { branchId }),
      ...(isConsultantUsed.value && { consultantUsed: true }),
      designerOrder: isDesignerProduct(product),
      rxOrder: false,
      productType: getProductType(product.value),
    } as FimBasketItemCustomData

    const addItemPayload = {
      variantId: a2bVariant.id,
      quantity: Number(quantity.value),
      ...(isEmpty(displayDataPayload) ? {} : displayDataPayload),
      ...(isEmpty(customData) ? {} : { customData }),
      ...(!promotionId && { promotionId: null }),
      ...(promotionId && !isBuyXGetYPrioritized.value && { promotionId }),
    }

    await addItem(addItemPayload as any)
    isAddingToCart.value = false
    openBasketOverlay()
    trackAddToBasket(a2bProduct, Number(quantity.value), a2bVariant, {
      source: payload?.source,
    })
  }
}

const stockQuantity = computed(() => selectedVariant.value?.stock.quantity)

const detailNameMapping = {
  modelName: $i18n.t('pdp.details.modelName'),
  ...(Boolean(product.value?.attributes?.sports) && {
    sports: $i18n.t('pdp.details.sports'),
  }),
}

const lowestPrice = computed(() =>
  getLowestPriorPrice(
    price.value,
    product.value?.lowestPriorPrice,
    Number(quantity.value),
  ),
)

const breadcrumbSchema = computed(() =>
  getBreadcrumbSchemaItems(
    breadcrumbs.value,
    currentShop.value.domain || '',
    route.fullPath,
    head.value.title || '',
  ),
)

// // Used for getting benefits-list and recommended plps from storyblok
const categorySlug = computed(() =>
  isReadyMade.value ? 'ready-made-glasses' : 'sunglasses',
)
const isReadyMade = computed(() => isReadyMadeGlasses(product.value))

const recommendationLinkFieldName =
  ref<RecommendationLinkFieldNames>('sunglasses_links')

setPageState('pageType', 'product')

// todo: this was a hack to get around UI not updating if I remember correctly. Check if still needed
const deliveryEstimatesKey = ref(0)
scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch([stock, inStock, isSellableWithoutStock, isA2bDisabled], () => {
    deliveryEstimatesKey.value += 1
  })

  // todo: sometimes product is null when this check is triggered, so I wrapped it in a watcher
  // this should never happen, because we're awaiting product fetching... need to investigate further
  watch(isReadyMade, (val) => {
    if (val) {
      recommendationLinkFieldName.value = 'ready_made_glasses_links'
      setPageState('productCategory', ProductCategories.readyMadeGlasses)
    } else {
      setPageState('productCategory', ProductCategories.sunglasses)
    }
  })

  watch([variantsSortedByPrice, faceWidth], () => checkAvailability(), {
    immediate: true,
  })
  watch(
    queryVariantId,
    (next) => {
      if (next) {
        setVariantId(next)
      }
    },
    { immediate: true },
  )
})

const productSchema = computed(() =>
  generateProductSchema(
    product.value,
    selectedVariant.value,
    price.value,
    context,
  ),
)

useJsonld(() => productSchema.value)
useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))
</script>
